<script setup lang="ts">
import type { Track } from '~/types'

import { useI18n } from 'vue-i18n'
import { clone, uniqBy } from 'lodash-es'
import { ref, computed } from 'vue'

import axios from 'axios'

import TrackMobileRow from '~/components/audio/track/MobileRow.vue'
import TrackModal from '~/components/audio/track/Modal.vue'

import Pagination from '~/ui/Pagination.vue'
import TrackRow from '~/components/audio/track/Row.vue'
import Input from '~/ui/Input.vue'
import Spacer from '~/ui/Spacer.vue'
import Loader from '~/ui/Loader.vue'
import Table from '~/ui/Table.vue'

import useErrorHandler from '~/composables/useErrorHandler'

interface Events {
  (e: 'fetched'): void
  (e: 'page-changed', page: number): void
}

interface Props {
  tracks?: undefined | Track[]

  showAlbum?: boolean
  showArtist?: boolean
  showPosition?: boolean
  positionFromTrack?: boolean
  showArt?: boolean
  showDuration?: boolean
  search?: boolean
  displayActions?: boolean
  isArtist?: boolean
  isAlbum?: boolean
  isPodcast?: boolean

  filters?: object

  nextUrl?: string | null

  paginateResults?: boolean
  total?: number
  page?: number
  paginateBy?: number

  unique?: boolean
}

const emit = defineEmits<Events>()
const props = withDefaults(defineProps<Props>(), {
  tracks: undefined,

  showAlbum: true,
  showArtist: true,
  showPosition: false,
  positionFromTrack: false,
  showArt: true,
  showDuration: true,
  search: false,
  displayActions: true,
  isArtist: false,
  isAlbum: false,
  isPodcast: false,

  filters: () => ({}),
  nextUrl: null,

  paginateResults: false,
  total: 0,
  page: 1,
  paginateBy: 25,

  unique: true
})

// TODO: Either use props only, or fetch here - otherwise `refetch` replaces parent's fetched tracks with another fetch

const currentPage = ref(props.page)
const page = ref(1)
const totalTracks = ref(props.total)
const fetchDataUrl = ref(props.nextUrl)
const additionalTracks = ref([] as Track[])
const query = ref('')

const allTracks = computed(() => {
  const tracks = [...(props.tracks ?? []), ...additionalTracks.value]
  return props.unique
    ? uniqBy(tracks, 'id')
    : tracks
})

const paginateResults = computed(() => props.paginateResults && allTracks.value.length > props.paginateBy)

const { t } = useI18n()

const labels = computed(() => ({
  title: t('components.audio.track.Table.table.header.title'),
  album: t('components.audio.track.Table.table.header.album'),
  artist: t('components.audio.track.Table.table.header.artist'),
  searchPlaceholder: t('views.Search.header.search')
}))

const isLoading = ref(false)
const fetchData = async () => {
  isLoading.value = true

  const params = {
    ...clone(props.filters),
    page_size: props.paginateBy,
    page: currentPage.value,
    include_channels: true,
    q: query.value
  }

  try {
    const response = await axios.get('tracks/', { params })

    // TODO (wvffle): Fetch continuously?
    fetchDataUrl.value = response.data.next
    additionalTracks.value = response.data.results
    totalTracks.value = response.data.count
    emit('fetched')
  } catch (error) {
    useErrorHandler(error as Error)
  }
  isLoading.value = false
}

const performSearch = () => {
  currentPage.value = 1
  additionalTracks.value = []
  fetchData()
}

if (props.tracks === undefined) {
  fetchData()
}

const updatePage = (page: number) => {
  if (props.tracks === undefined) {
    currentPage.value = page
    fetchData()
  } else {
    emit('page-changed', page)
  }
}

const showTrackModal = ref(false)
const modalTrack = ref<Track | null>(null)
const modalIndex = ref<number | null>(null)

function openTrackModal(track: Track, index: number) {
  showTrackModal.value = true
  modalTrack.value = track
  modalIndex.value = index
}
</script>

<template>
  <div>
    <!-- Show the search bar if search is true -->

    <Input
      v-if="search"
      v-model="query"
      search
      autofocus
      :placeholder="labels.searchPlaceholder"
      @search="performSearch"
      class="shape-field-full"
    />
    <Spacer v-if="search" />

    <!-- Add a header if needed -->

    <slot name="header" />

    <!-- Show a message if no tracks are available -->

    <slot
      v-if="!isLoading && allTracks.length === 0"
      name="empty-state"
    >
      <empty-state
        :refresh="true"
        @refresh="fetchData()"
      />
    </slot>

    <!-- Table on screens > 768px wide -->
    <!-- TODO: Make responsive to parent container instead of screen -->

    <div
      v-else
      class="tablet-and-up"
    >
      <Loader v-if="isLoading" />

      <Table
        :grid-template-columns="['56px', '56px', 'auto', 'auto', 'auto', '56px', '64px', ...(showDuration? ['48px' as const] : [])]"
        :header-props="{ 'table-header': true }"
        ui-gap="16"
      >
        <template #header>
          <label />
          <label />
          <label>
            <span>{{ labels.title }}</span>
          </label>
          <label>
            <span v-if="showAlbum">{{ labels.album }}</span>
          </label>
          <label>
            <span v-if="showArtist">{{ labels.artist }}</span>
          </label>
          <label />
          <label v-if="showDuration">
            <Icon
              icon="bi:clock"
            />
          </label>
          <label />
        </template>

        <!-- TODO: Inline this component to keep track of the columns -->
        <TrackRow
          v-for="(track, index) in allTracks"
          :key="`${track.id} ${track.position}`"
          :track
          :index
          :tracks="allTracks"
          :show-album
          :show-artist
          :show-position
          :position-from-track
          :show-art
          :display-actions
          :show-duration
          :is-podcast
        />
      </Table>

      <!-- Pagination -->

      <Pagination
        v-if="paginateResults"
        v-model:page="page"
        :pages="Math.ceil((totalTracks || 0)/paginateBy)"
        @update:current="updatePage"
      />
    </div>

    <!-- Under 768px screen width -->
    <!-- TODO: Make responsive to parent container instead of screen -->

    <div
      class="tablet-and-below"
    >
      <Loader v-if="isLoading" />

      <!-- For each item, build a row -->

      <track-mobile-row
        v-for="(track, index) in allTracks"
        :key="track.id"
        :track
        :index
        :show-position
        :show-art
        :show-duration
        :is-artist
        :is-album
        :is-podcast
        @open-modal="openTrackModal"
      />
      <!-- TODO: Replace with <PlayButton :dropdown-only="true"> after its display is fixed for mobile -->
      <track-modal
        v-if="modalTrack"
        v-model:show="showTrackModal"
        :track="modalTrack"
        :index="modalIndex ?? 0"
        :is-artist="isArtist"
        :is-album="isAlbum"
      />
      <Pagination
        v-if="paginateResults"
        v-model:page="page"
        :pages="Math.ceil((totalTracks || 0)/paginateBy)"
        @update:current="updatePage"
      />
    </div>
  </div>
</template>

<style scoped>
  [table-header] > label {
    padding-left: 4px;
    align-self: center !important;
  }
</style>
